import { TelegramWebAppUtils } from "../../shared/api/telegram";
import { HttpError, isHttpError, type AuthSnapshot, type FetcherConfig } from "../../shared/api/http";
import type { TokenPairDto } from "../../shared/contracts/model/tokenPairDto";
import type { AuthApi } from "./auth-api";
import type { TokensListener } from "./token-store";
import { TokenStore } from "./token-store";

const DEFAULT_BASE_URL =
  (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? "/api";

export const TELEGRAM_READY_TIMEOUT_MS = 5_000;

export interface RefreshResult {
  tokens: TokenPairDto | null;
  refreshed: boolean;
  superseded?: boolean;
}

export class TelegramSessionExpiredError extends HttpError {
  constructor() {
    super('InitData expired', 401);
    this.name = 'TelegramSessionExpiredError';
  }
}

// Type guard for token-like objects
interface TokenLike {
  accessToken?: string;
  token?: string;
}

function isTokenLike(value: unknown): value is TokenLike {
  return typeof value === "object" && value !== null;
}

interface RefreshTokenLike {
  refreshToken?: string;
}

function isRefreshTokenLike(value: unknown): value is RefreshTokenLike {
  return typeof value === "object" && value !== null && "refreshToken" in value;
}

const normalizeRefreshToken = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  if (isRefreshTokenLike(value)) {
    const token = value.refreshToken;
    if (typeof token === "string") {
      const trimmed = token.trim();
      return trimmed.length ? trimmed : undefined;
    }
  }
  return undefined;
};

// Safely extract a usable token string from various shapes
const getTokenString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  if (isTokenLike(value)) {
    const maybeAccess = value.accessToken ?? value.token;
    if (typeof maybeAccess === "string") {
      const trimmed = maybeAccess.trim();
      return trimmed.length ? trimmed : undefined;
    }
  }
  return undefined;
};

export class AuthService {
  private apiBaseUrl = DEFAULT_BASE_URL;
  private authGeneration = 0;
  private expiredTelegramInitData: string | null = null;
  private sessionError: TelegramSessionExpiredError | null = null;
  private readonly sessionErrorListeners = new Set<(error: TelegramSessionExpiredError | null) => void>();
  private readonly telegramAuthRequests = new Map<string, Promise<TokenPairDto>>();
  private readonly refreshesInFlight = new Map<
    string,
    Promise<RefreshResult>
  >();
  private readonly telegramReauthenticationsInFlight = new Map<
    number,
    Promise<boolean>
  >();

  constructor(
    private readonly api: AuthApi,
    private readonly tokenStore: TokenStore,
    private readonly logger: typeof console = console,
  ) {}

  getApiBaseUrl(): string {
    return this.apiBaseUrl;
  }

  setApiBaseUrl(url: string): void {
    this.apiBaseUrl = url;
  }

  private getPublicFetcherConfig(): FetcherConfig {
    return { baseUrl: this.apiBaseUrl };
  }

  subscribeSessionError(listener: (error: TelegramSessionExpiredError | null) => void): () => void {
    this.sessionErrorListeners.add(listener);
    listener(this.sessionError);
    return () => this.sessionErrorListeners.delete(listener);
  }

  private setSessionError(error: TelegramSessionExpiredError | null): void {
    if (this.sessionError === error) return;
    this.sessionError = error;
    for (const listener of this.sessionErrorListeners) listener(error);
  }

  private async requestTelegramAuthentication(initData: string): Promise<TokenPairDto> {
    // Repeating a launch payload explicitly rejected as expired cannot make it
    // fresh. Keep the latch in memory; reopening through Telegram supplies a new
    // payload, and valid refresh tokens remain usable independently of its age.
    if (initData === this.expiredTelegramInitData) {
      throw this.sessionError ?? new TelegramSessionExpiredError();
    }
    const generation = this.authGeneration;
    const key = `${generation}:${initData}`;
    const existing = this.telegramAuthRequests.get(key);
    if (existing) return existing;
    const request = this.api.telegramAuth(initData, this.getPublicFetcherConfig()).catch(error => {
      if (isHttpError(error) && error.status === 401 && /^initdata expired$/i.test(error.message.trim())) {
        this.expiredTelegramInitData = initData;
        const expired = new TelegramSessionExpiredError();
        // A delayed failure from an old session must not interrupt a newer login.
        if (this.authGeneration === generation) this.setSessionError(expired);
        throw expired;
      }
      throw error;
    });
    this.telegramAuthRequests.set(key, request);
    try {
      return await request;
    } finally {
      if (this.telegramAuthRequests.get(key) === request) this.telegramAuthRequests.delete(key);
    }
  }

  private async getAuthSnapshot(): Promise<AuthSnapshot> {
    if (!getTokenString(this.tokenStore.getAccessToken())) {
      await this.loadTokens();
    }
    return {
      sessionId: this.authGeneration,
      token: getTokenString(this.tokenStore.getAccessToken()),
    };
  }

  getFetcherConfig(): FetcherConfig {
    return {
      baseUrl: this.apiBaseUrl,
      getAuthSessionId: () => this.authGeneration,
      getAuthSnapshot: () => this.getAuthSnapshot(),
      isAuthSessionCurrent: (expectedSessionId) =>
        expectedSessionId === this.authGeneration,
      clearAuthSession: async (expectedSessionId) =>
        typeof expectedSessionId === "number" &&
        this.clearTokensIfGeneration(expectedSessionId),
      getToken: async () => {
        // Always try in-memory first for speed
        const cached = getTokenString(this.tokenStore.getAccessToken());
        if (cached) return cached;

        // Lazy-load tokens if they were not loaded yet (e.g., loader ran before AuthLayout)
        const loaded = await this.loadTokens();
        return getTokenString(loaded?.accessToken);
      },
      onRefresh: async (expectedSessionId) => {
        const expectedGeneration =
          typeof expectedSessionId === "number"
            ? expectedSessionId
            : this.authGeneration;
        try {
          const result = await this.refreshTokens(
            undefined,
            expectedGeneration,
          );
          if (this.authGeneration !== expectedGeneration || result.superseded) {
            return false;
          }
          if (!result.refreshed) {
            return this.recoverTelegramSession(expectedGeneration);
          }

          // After refresh, verify tokens match current Telegram user
          const currentTelegramId =
            TelegramWebAppUtils.getInitDataUnsafe()?.user?.id;
          if (currentTelegramId && result.tokens) {
            const payload = this.decodeJwtPayload(result.tokens.accessToken);
            const tokenTelegramId = payload?.telegramId ?? payload?.telegram_id;
            if (
              tokenTelegramId &&
              Number(tokenTelegramId) !== Number(currentTelegramId)
            ) {
              this.logger.warn(
                `[AuthService] Refreshed tokens don't match current Telegram user: token has ${tokenTelegramId}, expected ${currentTelegramId}. Re-authenticating.`,
              );
              return this.recoverTelegramSession(expectedGeneration);
            }
          }

          return this.authGeneration === expectedGeneration;
        } catch (error) {
          this.logger.warn("[AuthService] Token refresh failed:", error);
          // A network/server outage does not revoke the stored refresh token.
          // Falling back to an old launch payload would turn it into a forced
          // logout even though a later refresh could recover the session.
          return isHttpError(error) && error.status === 401
            ? this.recoverTelegramSession(expectedGeneration)
            : false;
        }
      },
    };
  }

  private async recoverTelegramSession(
    expectedGeneration: number,
  ): Promise<boolean> {
    if (this.authGeneration !== expectedGeneration) {
      return false;
    }

    try {
      const recovered = await this.reauthenticateWithTelegram(
        expectedGeneration,
      );
      if (recovered) return true;
    } catch (error) {
      this.logger.warn("[AuthService] Telegram re-authentication failed:", error);
    }

    // Never let a failed request from an older session clear a newer one.
    await this.clearTokensIfGeneration(expectedGeneration);
    return false;
  }

  private async reauthenticateWithTelegram(
    expectedGeneration: number,
  ): Promise<boolean> {
    const existing = this.telegramReauthenticationsInFlight.get(
      expectedGeneration,
    );
    if (existing) return existing;

    const inFlight = this.performTelegramReauthentication(expectedGeneration);
    this.telegramReauthenticationsInFlight.set(expectedGeneration, inFlight);
    try {
      return await inFlight;
    } finally {
      if (
        this.telegramReauthenticationsInFlight.get(expectedGeneration) ===
        inFlight
      ) {
        this.telegramReauthenticationsInFlight.delete(expectedGeneration);
      }
    }
  }

  private async performTelegramReauthentication(
    expectedGeneration: number,
  ): Promise<boolean> {
    if (this.authGeneration !== expectedGeneration) return false;

    const initData = TelegramWebAppUtils.getInitData();
    if (!initData) return false;

    const tokens = await this.requestTelegramAuthentication(initData);
    if (this.authGeneration !== expectedGeneration) return false;

    await this.tokenStore.setTokens(tokens);
    this.setSessionError(null);
    return (
      this.authGeneration === expectedGeneration &&
      this.tokenStore.getAccessToken() === tokens.accessToken &&
      this.tokenStore.getRefreshToken() === tokens.refreshToken
    );
  }

  async loadTokens(): Promise<TokenPairDto | null> {
    return this.tokenStore.load();
  }

  getTokens(): TokenPairDto | null {
    return this.tokenStore.getTokens();
  }

  getAccessToken(): string | null {
    return this.tokenStore.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenStore.getRefreshToken();
  }

  subscribe(listener: TokensListener): () => void {
    return this.tokenStore.subscribe(listener);
  }

  async signInWithInitData(initData: string): Promise<TokenPairDto> {
    const tokens = await this.requestTelegramAuthentication(initData);
    this.authGeneration += 1;
    await this.tokenStore.setTokens(tokens);
    this.setSessionError(null);
    return tokens;
  }

  async signInFromTelegram(): Promise<TokenPairDto> {
    const initData = TelegramWebAppUtils.getInitData();
    if (!initData) {
      throw new Error(
        "Telegram initData is missing. Open the app inside Telegram Mini Apps.",
      );
    }
    return this.signInWithInitData(initData);
  }

  async refreshTokens(
    refreshToken?: string,
    expectedGeneration: number = this.authGeneration,
  ): Promise<RefreshResult> {
    await this.loadTokens();
    if (this.authGeneration !== expectedGeneration) {
      return this.getSupersededRefreshResult();
    }

    const generation = this.authGeneration;
    const storedTokenAtStart = normalizeRefreshToken(
      this.tokenStore.getRefreshToken(),
    );
    const tokenValue =
      normalizeRefreshToken(refreshToken) ?? storedTokenAtStart;

    if (!tokenValue) {
      return { tokens: this.tokenStore.getTokens(), refreshed: false };
    }

    const refreshKey = `${generation}:${tokenValue}`;
    const existing = this.refreshesInFlight.get(refreshKey);
    if (existing) return existing;

    const mayPersist = !storedTokenAtStart || storedTokenAtStart === tokenValue;
    const inFlight = this.performTokenRefresh(
      tokenValue,
      generation,
      storedTokenAtStart,
      mayPersist,
    );
    this.refreshesInFlight.set(refreshKey, inFlight);
    try {
      return await inFlight;
    } finally {
      if (this.refreshesInFlight.get(refreshKey) === inFlight) {
        this.refreshesInFlight.delete(refreshKey);
      }
    }
  }

  private async performTokenRefresh(
    tokenValue: string,
    generation: number,
    storedTokenAtStart: string | undefined,
    mayPersist: boolean,
  ): Promise<RefreshResult> {
    if (this.authGeneration !== generation) {
      return this.getSupersededRefreshResult();
    }
    if (
      mayPersist &&
      normalizeRefreshToken(this.tokenStore.getRefreshToken()) !==
        storedTokenAtStart
    ) {
      return this.getSupersededRefreshResult();
    }

    const tokens = await this.api.refresh(
      tokenValue,
      this.getPublicFetcherConfig(),
    );
    if (this.authGeneration !== generation) {
      return this.getSupersededRefreshResult();
    }
    if (!mayPersist) {
      return { tokens, refreshed: true };
    }
    const currentStoredToken = normalizeRefreshToken(
      this.tokenStore.getRefreshToken(),
    );
    if (currentStoredToken !== storedTokenAtStart) {
      return this.getSupersededRefreshResult();
    }
    await this.tokenStore.setTokens(tokens);
    if (
      this.authGeneration !== generation ||
      normalizeRefreshToken(this.tokenStore.getRefreshToken()) !==
        normalizeRefreshToken(tokens.refreshToken)
    ) {
      return this.getSupersededRefreshResult();
    }
    return { tokens, refreshed: true };
  }

  private getSupersededRefreshResult(): RefreshResult {
    return {
      tokens: this.tokenStore.getTokens(),
      refreshed: false,
      superseded: true,
    };
  }

  async ensureFreshTokens(): Promise<TokenPairDto | null> {
    const currentTokens = await this.loadTokens();
    if (!currentTokens) {
      return null;
    }

    return currentTokens;
  }

  async logout(options?: { skipServer?: boolean }): Promise<void> {
    const skipServer = options?.skipServer ?? false;
    const accessToken = this.tokenStore.getAccessToken();

    if (!skipServer && accessToken) {
      try {
        await this.api.logout(this.getFetcherConfig());
      } catch (error) {
        if (
          !(error instanceof Error) ||
          typeof error.message !== "string" ||
          !error.message.includes("HTTP 401")
        ) {
          throw error;
        }
      }
    }

    await this.clearTokens();
  }

  async clearTokens(): Promise<void> {
    this.authGeneration += 1;
    await this.tokenStore.setTokens(null);
  }

  async clearTokensIfGeneration(expectedGeneration: number): Promise<boolean> {
    if (this.authGeneration !== expectedGeneration) {
      return false;
    }
    this.authGeneration += 1;
    await this.tokenStore.setTokens(null);
    return true;
  }

  async ensureTelegramReady() {
    // init.js loads the SDK asynchronously; hydration can win that race on a
    // slow connection even when Telegram supplied a valid launch payload.
    const deadline = Date.now() + TELEGRAM_READY_TIMEOUT_MS;
    while (!TelegramWebAppUtils.isAvailable()) {
      if (Date.now() >= deadline) {
        throw new Error("Telegram SDK could not load. Check your connection and reopen the app.");
      }
      await new Promise<void>(resolve => setTimeout(resolve, 50));
    }
    try {
      TelegramWebAppUtils.lifecycle.ready();
    } catch (error) {
      this.logger.warn(
        "[AuthService] Unable to mark Telegram WebApp ready",
        error,
      );
    }
  }

  private decodeJwtPayload(
    token: string,
  ): { telegramId?: number; telegram_id?: number; sub?: string } | null {
    try {
      const [, payloadPart] = token.split(".");
      if (!payloadPart) return null;
      const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized.padEnd(
        Math.ceil(normalized.length / 4) * 4,
        "=",
      );
      const json = atob(padded);
      return JSON.parse(json);
    } catch (error) {
      this.logger.warn("[AuthService] Failed to decode JWT payload", error);
      return null;
    }
  }
}
